import { AppConstants } from '@app/app.constants';
import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { User } from './user';
import { Place } from './place';

export interface _Waiter {
    status: string;
    place: number;
    photo: {
        url: string;
        b64: any;
    },
    user: number;
    alias: string,
    allow: string,
    email: string;
    pin: string;        // PIN NUEVO en claro (SOLO escritura: al ponerlo). El server
                        // lo hashea; nunca vuelve al cliente.
    oldpin?: string;    // PIN ACTUAL en claro (SOLO escritura): el server lo exige
                        // para cambiar el PIN propio. No es columna; se consume.
    has_pin: boolean;   // ¿tiene PIN configurado? (lo computa el server). SOLO lectura.
    rfid: string;       // PULSERA RFID nueva (SOLO escritura: al asignarla). El server
                        // la guarda y resuelve; nunca vuelve al cliente.
    clearrfid?: boolean;// QUITAR la pulsera (SOLO escritura, transitorio: no es columna).
    rfidreassign?: boolean; // la pulsera nueva se la QUITA a quien la tenga (SOLO escritura, transitorio).
    has_rfid: boolean;  // ¿tiene pulsera asignada? (lo computa el server). SOLO lectura.
};

interface _WaiterData extends _Waiter {
    objid?: number;
    _uuid?: string;
    created?: Date;
};

abstract class WaiterData extends DataObject {
    protected _waiter: _WaiterData = {
        status: null,
        place: null,
        user: null,
        photo: {
            url: null,
            b64: null
        },
        alias: null,
        allow: null,
        email: null,
        pin: null,
        oldpin: null,
        has_pin: null,
        rfid: null,
        clearrfid: null,
        rfidreassign: null,
        has_rfid: null
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);
        this._waiter.created = new Date();
    }

    /****************************/
    /* STAFF PERMISSION FLAGS   */
    /****************************/

    protected AllowFlags = Object.freeze({
        StaffManage: 0,     // allow to create or edit staff members
        OfferManage: 1,     // allow to create or edit offers
        ProductManage: 2,   // allow to create or edit products
        TableManage: 3,     // allow to create or edit tables
        ExtraManage: 4,     // allow to create or edit table suplements
        PlaceManage: 5,     // allow to edit place details
        LicenceManage: 6,   // allow to pay application license
        CanCompliment: 7,   // allow to compliment on ticket items 
        CanAvailable: 8,    // allow to set an item as available / unavailable
        POSManage: 9,       // allow to configure POS details (as printers)
        ReportAccess: 10,   // allow to review place reports
        TillManage: 11,     // allow to control/open the till 
        CanCancel: 12,      // allow to cancel / reject tickets
        CanModify: 14,      // allow to modify created tickets
        CanReturn: 15,      // allow to return paid products on tickets
        CanReopen: 16,      // allow to reopen closed tickets
        TillOpen: 17,       // allow to open the till cash register 
        ClientAdd: 18       // allow to add new clients
    });

    protected get allow(): Array<any> {
        let _array = Object.keys(this.AllowFlags).map(() => false);

        if (this._waiter.allow){
            let _flags = Array.from(this._waiter.allow);
            for(let i=0; i < _flags.length; i++){
                _array[i] = (_flags[i] == '1');
            }        
        }

        return _array;
    }

    protected set allow(value: Array<any>) {
        let _array = [];
        for(let i=0; i < value.length; i++){
            _array[i] = (value[i] === true) ? '1' : '0';
        }

        let _flags = _array.join(''); 
        if(this.patchValue(this._waiter, 'allow', _flags)){
            this.ToUpdate = true;
        }
    }

    protected _setFlagValue(flag, value){
        let _flags = this.allow;
        _flags[flag] = value;
        this.allow = _flags;        
    }

    get AllowStaffManage(){
        return this.allow[this.AllowFlags.StaffManage];
    }

    set AllowStaffManage(value: boolean){
        this._setFlagValue(this.AllowFlags.StaffManage, value);
    }

    get AllowOfferManage(){
        return this.allow[this.AllowFlags.OfferManage];
    }

    set AllowOfferManage(value: boolean){
        this._setFlagValue(this.AllowFlags.OfferManage, value);
    }

    get AllowProductManage(){
        return this.allow[this.AllowFlags.ProductManage];
    }

    set AllowProductManage(value: boolean){
        this._setFlagValue(this.AllowFlags.ProductManage, value);
    }

    get AllowTableManage(){
        return this.allow[this.AllowFlags.TableManage];
    }

    set AllowTableManage(value: boolean){
        this._setFlagValue(this.AllowFlags.TableManage, value);
    }

    get AllowExtraManage(){
        return this.allow[this.AllowFlags.ExtraManage];
    }

    set AllowExtraManage(value: boolean){
        this._setFlagValue(this.AllowFlags.ExtraManage, value);
    }

    get AllowPlaceManage(){
        return this.allow[this.AllowFlags.PlaceManage];
    }

    set AllowPlaceManage(value: boolean){
        this._setFlagValue(this.AllowFlags.PlaceManage, value);
    }

    get AllowLicenceManage(){
        return this.allow[this.AllowFlags.LicenceManage];
    }

    set AllowLicenceManage(value: boolean){
        this._setFlagValue(this.AllowFlags.LicenceManage, value);
    }

    get AllowCompliment(){
        return this.allow[this.AllowFlags.CanCompliment];
    }

    set AllowCompliment(value: boolean){
        this._setFlagValue(this.AllowFlags.CanCompliment, value);
    }

    get AllowAvailability(){
        return this.allow[this.AllowFlags.CanAvailable];
    }

    set AllowAvailability(value: boolean){
        this._setFlagValue(this.AllowFlags.CanAvailable, value);
    }

    get AllowPOSManage(){
        return this.allow[this.AllowFlags.POSManage];
    }

    set AllowPOSManage(value: boolean){
        this._setFlagValue(this.AllowFlags.POSManage, value);
    }

    get AllowReports(){
        return this.allow[this.AllowFlags.ReportAccess];
    }

    set AllowReports(value: boolean){
        this._setFlagValue(this.AllowFlags.ReportAccess, value);
    }

    get AllowTillManage(){
        return this.allow[this.AllowFlags.TillManage];
    }

    set AllowTillManage(value: boolean){
        this._setFlagValue(this.AllowFlags.TillManage, value);
    }

    get AllowTillOpen(){
        return this.allow[this.AllowFlags.TillOpen];
    }

    set AllowTillOpen(value: boolean){
        this._setFlagValue(this.AllowFlags.TillOpen, value);
    }

    get AllowTicketReopen(){
        return this.allow[this.AllowFlags.CanReopen];
    }

    set AllowTicketReopen(value: boolean){
        this._setFlagValue(this.AllowFlags.CanReopen, value);
    }

    get AllowTicketCancel(){
        return this.allow[this.AllowFlags.CanCancel];
    }

    set AllowTicketCancel(value: boolean){
        this._setFlagValue(this.AllowFlags.CanCancel, value);
    }

    get AllowTicketUpdate(){
        return this.allow[this.AllowFlags.CanModify];
    }

    set AllowTicketUpdate(value: boolean){
        this._setFlagValue(this.AllowFlags.CanModify, value);
    }

    get AllowTicketReturn(){
        return this.allow[this.AllowFlags.CanReturn];
    }

    set AllowTicketReturn(value: boolean){
        this._setFlagValue(this.AllowFlags.CanReturn, value);
    }

    get AllowClientAdd(){
        return this.allow[this.AllowFlags.ClientAdd];
    }

    set AllowClientAdd(value: boolean){
        this._setFlagValue(this.AllowFlags.ClientAdd, value);
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(){
        return this._waiter.created;
    }

    get status(): string{
        return this._waiter.status;
    }

    set status(value: string){
        if (this.patchValue(this._waiter, 'status', value)){
            this.ToUpdate = true;
        }
    }

    get place() : Place {
        return this._children['place'] || null;
    }

    set place(value: Place) {
        if (this.SetChild('place', value, 'place')){
            this.ToUpdate = true;
        }
    }

    get photo(): string{
        return this._waiter.photo.url;
    }

    set photo(value: string){
        if (this.patchValue(this._waiter.photo, 'url', value)){
            this.ToUpdate = true;
        }
    }
   
    get base64(): any {
        return this._waiter.photo.b64;
    }

    set base64(value: any){
        if (this.patchValue(this._waiter.photo, 'b64', value)){
            this.ToUpdate = true;
        }
    }

    get alias(): string{
        return this._waiter.alias;
    }

    set alias(value: string){
        if (this.patchValue(this._waiter, 'alias', value)){
            this.ToUpdate = true;
        }
    }

    // PIN NUEVO en claro (solo escritura: al ponerlo). El server lo hashea y la
    // unicidad por local se valida server-side. Tras cargar, es null (el server
    // nunca devuelve el PIN/hash): has_pin indica si hay uno configurado.
    get pin(): string{
        return this._waiter.pin;
    }

    set pin(value: string){
        let _norm = (value != null && ('' + value).trim() != '') ? ('' + value).trim() : null;
        if (this.patchValue(this._waiter, 'pin', _norm)){
            this.ToUpdate = true;
        }
    }

    // PIN ACTUAL (write-only): se envía junto al nuevo para verificar server-side que
    // quien cambia su propio PIN conoce el actual. No se carga ni se muestra.
    set oldpin(value: string){
        let _norm = (value != null && ('' + value).trim() != '') ? ('' + value).trim() : null;
        this.patchValue(this._waiter, 'oldpin', _norm);
    }

    get has_pin(): boolean {
        return !!(this._waiter.has_pin || this._waiter.pin);
    }

    // PULSERA RFID nueva (solo escritura: al asignarla). Tras cargar es null (el
    // server nunca devuelve el tag): has_rfid indica si hay una asignada.
    get rfid(): string {
        return this._waiter.rfid;
    }

    set rfid(value: string){
        let _norm = (value != null && ('' + value).trim() != '') ? ('' + value).trim() : null;
        if (this.patchValue(this._waiter, 'rfid', _norm)){
            if (_norm){
                this._waiter.clearrfid = null;    // asignar y quitar son excluyentes
            }
            this.ToUpdate = true;
        }
    }

    // REASIGNAR: la pulsera nueva ya la tiene otro (rfidcheck.php) y el usuario confirmó
    // quitársela. Transitorio; el server se la quita al anterior en la misma transacción.
    set rfidreassign(value: boolean){
        if (this.patchValue(this._waiter, 'rfidreassign', value ? true : null)){
            this.ToUpdate = true;
        }
    }

    // QUITAR la pulsera (transitorio): el server pone STAFF.rfid a NULL.
    set clearrfid(value: boolean){
        if (this.patchValue(this._waiter, 'clearrfid', value ? true : null)){
            if (value){
                this._waiter.rfid = null;
            }
            this.ToUpdate = true;
        }
    }

    get has_rfid(): boolean {
        if (this._waiter.clearrfid){
            return false;
        }
        return !!(this._waiter.has_rfid || this._waiter.rfid);
    }

    get user() : User {
        return this._children['user'] || null;
    }

    set user(value: User) {
        if (this.SetChild('user', value, 'user')){
            this.ToUpdate = true;
        }
    }

    get email(): string {
        return this._waiter.email;
    }

    set email(value: string) {
        // Normalizar a minúsculas (y sin espacios): los validadores del alta y el
        // servidor (#1232) tratan el correo en minúsculas, pero antes se GUARDABA
        // tal cual se tecleaba. Un camarero creado con mayúsculas (p.ej.
        // "Pepe@Gmail.com") no casaba al registrarse/validar -> fallo. Normalizar
        // aquí cubre el form, el cambio de correo y la propagación a user.email.
        let _norm = value ? value.trim().toLowerCase() : value;
        if (this.patchValue(this._waiter, 'email', _norm)){
            this.ToUpdate = true;
        }
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return (async () => {
            let _base64 = this.base64;
            if (!_base64){
                let _uploadUrl = AppConstants.baseURL + AppConstants.uploadPath;
                if (this.photo && !this.photo.startsWith(_uploadUrl)){
                    this.base64 = await this.uploadTobase64(this.photo);
                }
            }
    
            return {
                place: this._waiter.place,
                status: this._waiter.status,
                user: this._waiter.user,
                photo: {
                    url: this.uploadToMysql(this.photo),
                    b64: this.base64
                },
                alias: this._waiter.alias,
                allow: this._waiter.allow,
                email: this._waiter.email,
                pin: this._waiter.pin,
                oldpin: this._waiter.oldpin,
                rfid: this._waiter.rfid,
                clearrfid: this._waiter.clearrfid,
                rfidreassign: this._waiter.rfidreassign
            };
        })();
    }

    protected get Depend() {
        return {
            place: { item: this.place, relation_info: { to: 'waiters', by: 'place' } },     // this[by -> 'place'][to -> 'waiters'] => this
            user: { item: this.user, relation_info: { to: 'waiter', by: 'user' } }          // this[by -> 'user'][to -> 'waiter'] => this
        };
    }

    protected get Children(){
        return [ /* empty */ ];
    }

    /****************************/
    /* DATA OBJECT              */
    /****************************/
    
    private _patchData(_waiter: _Waiter){
        let _toUpdate = false;

        _toUpdate = this.patchValue(this._waiter, 'status', _waiter['status']) || _toUpdate;
        _toUpdate = this.patchValue(this._waiter, 'place', _waiter['place']) || _toUpdate;
        _toUpdate = this.patchValue(this._waiter, 'photo', _waiter['photo']) || _toUpdate;
        _toUpdate = this.patchValue(this._waiter, 'user', _waiter['user']) || _toUpdate;
        _toUpdate = this.patchValue(this._waiter, 'alias', _waiter['alias']) || _toUpdate;
        _toUpdate = this.patchValue(this._waiter, 'allow', _waiter['allow']) || _toUpdate;
        _toUpdate = this.patchValue(this._waiter, 'email', _waiter['email']) || _toUpdate;
        // OJO: NO parchear 'pin' desde datos cargados — el server nunca lo envía
        // (solo has_pin) y parchearlo pisaría un PIN nuevo pendiente de enviar.
        _toUpdate = this.patchValue(this._waiter, 'has_pin', _waiter['has_pin']) || _toUpdate;
        _toUpdate = this.patchValue(this._waiter, 'has_rfid', _waiter['has_rfid']) || _toUpdate;

        return _toUpdate;
    }

    set Data(_waiter: _Waiter){
        this.patchValue(this._waiter, 'created', _waiter['created']);
        
        if (this._patchData(_waiter)){
            this.ToUpdate = true;
        }
    }

    get Info(){
        return this._waiter;
    }

    set Info(value){
        this.DoPatchValues(value);
    }

    private DoPatchValues(_waiter: _Waiter){
        this._patchData(_waiter)

        if (_waiter['user']){      // update children: 'user'
            let _objid = _waiter['user'].toString();
            this.SetChild('user', new User(_objid, this.data, this._objoptions), 'user');
        }
        else {
            this.SetChild('user', null, 'user');
        }

        if (_waiter['place']){      // update children: 'place'
            let _objid = _waiter['place'].toString();
            this.SetChild('place', new Place(_objid, this.data, this._objoptions), 'place');
        }
        else {
            this.SetChild('place', null, 'place');
        }
    }

    private _ddbb(info): _WaiterData {
        let _waiter: _WaiterData = {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            status: info['status'],
            place: info['place'] ? parseInt(info['place']) : null,
            user: info['user'] ? parseInt(info['user']) : null,
            photo: {
                url: this.mysqlToUpload(info['photo']),
                b64: null
            },        
            alias: info['alias'],
            allow: info['allow'],
            email: info['email'],
            pin: null,                      // el server nunca envía el PIN/hash
            has_pin: !!info['has_pin'],
            rfid: null,                     // el server nunca envía el tag
            clearrfid: null,
            rfidreassign: null,
            has_rfid: !!info['has_rfid']
        };

        return _waiter;
    }

    protected _OnUpdate(info){
        let _waiter = this._ddbb(info);
        this.patchValue(this._waiter, 'objid', _waiter['objid']);
        this.patchValue(this._waiter, 'created', _waiter['created']);
        this.DoPatchValues(_waiter);

        // El PIN NUEVO en claro solo vive en memoria hasta que el server confirma
        // la escritura. Aquí (echo del server tras aceptar el cambio) ya está
        // persistido y hasheado: lo borramos para no retenerlo más de lo necesario.
        // Se PRESERVA mientras haya un cambio local sin enviar (ToUpdate/ToInsert):
        // en ese caso el echo del server o bien no llega a _OnUpdate (lo gatea
        // _CanUpdate), o es un ForceRefresh con PIN pendiente -> no lo tocamos.
        // Asignación directa (no el setter) para no re-marcar ToUpdate.
        this._ClearCredentialTransients();
    }

    // El eco del server tras un commit puede ser IDÉNTICO al anterior (p.ej. cambiar el PIN
    // o la pulsera cuando has_pin/has_rfid ya estaban a 1): base.OnUpdate no llama entonces
    // a _OnUpdate (lo gatea _equal) y los transitorios se quedarían en Info y se
    // REENVIARÍAN en el siguiente commit de la ficha (una reasignación de pulsera repetida
    // sin confirmar, o un oldpin ya caducado). Se limpian aquí en cualquier caso.
    protected OnUpdate(info): boolean {
        let _changed = super.OnUpdate(info);
        this._ClearCredentialTransients();
        return _changed;
    }

    private _ClearCredentialTransients(){
        if (this._waiter.pin && !this.ToUpdate && !this.ToInsert){
            this._waiter.pin = null;
            this._waiter.oldpin = null;
        }
        if ((this._waiter.rfid || this._waiter.clearrfid || this._waiter.rfidreassign) && !this.ToUpdate && !this.ToInsert){
            this._waiter.rfid = null;
            this._waiter.clearrfid = null;
            this._waiter.rfidreassign = null;
        }
    }
}

export class Waiter extends WaiterData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('STAFF', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): Waiter {
        return this._Copy(store) as Waiter;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get status(): string {
        return super.status;
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        super.status = value;

        if (this.place){
            if ((this.status == 'DE') && (this.ToInsert) && (!this.CopyOf || this.CopyOf.ToInsert)){
                this.place.DelWaiter(this);
            }
            else {
                this.place.DoRefresh('STAFF');
            }    
        }
    }

    get avatar() : string {
        // Foto subida; si no hay, avatar generado. Cae al ALIAS cuando el user no
        // tiene nombre (camarero sin nombre -> avatarPhoto(null) daba null y la
        // cabecera quedaba sin avatar), para mostrar siempre algo con sentido.
        return super.photo || this.avatarPhoto((this.user ? this.user.fullname : null) || this.alias);
    }

    get hasPhoto() : boolean {
        return (super.photo && !this.isAvatarPhoto(super.photo));
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(){
        return (['PA', 'AC'].includes(this.status));
    }

    get IsActive(){
        return (this.status == 'AC');
    }

    get IsOnline(){
        return this.user.sessions.some(
        (session) => {
            return session.IsActive;
        });
    }
}
